// src/components/CustomerOrderPage/MealRatingPrompt.jsx
//
// Shown on the tracker once the order is Delivered. The guest rates the night
// overall and, if they want, each dish.
//
// Constraints this is designed around: the reader is holding a phone at a party
// with food in front of them. So it is taps, not typing; the overall score is
// one gesture and submittable on its own; per-dish is optional and collapsed
// behind that first tap; and the comment box only appears once they have
// engaged. Nobody is filling a form here.
//
// The station that cooked each dish is resolved server-side from the kitchen
// ticket, so nothing about routing is sent from or trusted to the browser.

import React, { useEffect, useState } from 'react';
import api, { getGuestHeaders } from '../../utils/api';

const STAR_LABELS = ['Poor', 'Fair', 'Good', 'Great', 'Excellent'];

const Stars = ({ value, onChange, size = 34, label }) => (
	<div role="radiogroup" aria-label={label} style={{ display: 'flex', gap: 2 }}>
		{[1, 2, 3, 4, 5].map((n) => (
			<button
				key={n}
				type="button"
				role="radio"
				aria-checked={value === n}
				aria-label={`${n} out of 5${STAR_LABELS[n - 1] ? `, ${STAR_LABELS[n - 1]}` : ''}`}
				onClick={() => onChange(n)}
				style={{
					background: 'none',
					border: 'none',
					padding: 2,
					cursor: 'pointer',
					fontSize: size,
					lineHeight: 1,
					color: n <= value ? '#FB8601' : 'rgba(17,17,19,0.22)',
					// Comfortably above the 24px minimum target size, since this is
					// being tapped one-handed.
					minWidth: size + 6,
					minHeight: size + 6,
					transition: 'color 0.15s ease',
				}}
			>
				★
			</button>
		))}
	</div>
);

const MealRatingPrompt = ({ orderId, items = [] }) => {
	const [overall, setOverall] = useState(0);
	const [perMeal, setPerMeal] = useState({}); // mealId -> score
	const [comment, setComment] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(false);
	const [error, setError] = useState(null);

	// A guest who re-scans the QR should see the stars they already gave, not a
	// blank form inviting them to rate twice.
	useEffect(() => {
		if (!orderId) return undefined;
		let alive = true;
		api.get(`/customer/orders/${orderId}/ratings`, { headers: getGuestHeaders() })
			.then((res) => {
				if (!alive) return;
				const existingOverall = res.data?.overall;
				const existingMeals = res.data?.meals || [];
				if (existingOverall || existingMeals.length > 0) {
					setDone(true);
					setOverall(existingOverall?.score || 0);
					setPerMeal(
						existingMeals.reduce((acc, m) => ({ ...acc, [m.mealId]: m.score }), {}),
					);
				}
			})
			.catch(() => { /* no prior rating, or offline: show the empty form */ });
		return () => { alive = false; };
	}, [orderId]);

	// Distinct dishes only. A guest who ordered two portions of jollof rates the
	// dish once, not once per portion.
	const dishes = React.useMemo(() => {
		const seen = new Map();
		items.forEach((item) => {
			const id = item?._id;
			if (id && !seen.has(id)) seen.set(id, item.name);
		});
		return Array.from(seen, ([id, name]) => ({ id, name }));
	}, [items]);

	const handleSubmit = async () => {
		if (overall === 0 && Object.keys(perMeal).length === 0) return;
		setSubmitting(true);
		setError(null);
		try {
			await api.post(
				`/customer/orders/${orderId}/ratings`,
				{
					overall: overall > 0 ? { score: overall, comment: comment.trim() } : undefined,
					meals: Object.entries(perMeal).map(([mealId, score]) => ({ mealId, score })),
				},
				{ headers: getGuestHeaders() },
			);
			setDone(true);
		} catch (err) {
			setError(err.response?.data?.msg || 'That did not send. Try once more.');
		} finally {
			setSubmitting(false);
		}
	};

	if (done) {
		return (
			<div
				style={{
					marginTop: 20,
					padding: 16,
					borderRadius: 12,
					background: 'rgba(61,30,109,0.05)',
					textAlign: 'center',
				}}
			>
				<div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
					Thanks for the feedback.
				</div>
				<div className="ev-subtle" style={{ fontSize: 13 }}>
					It goes straight to the team who cooked and served it.
				</div>
			</div>
		);
	}

	return (
		<div
			style={{
				marginTop: 20,
				padding: 16,
				borderRadius: 12,
				border: '1px solid rgba(17,17,19,0.10)',
				background: 'white',
				textAlign: 'left',
			}}
		>
			<div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>How was it?</div>
			<div className="ev-subtle" style={{ fontSize: 13, marginBottom: 12 }}>
				One tap is enough.
			</div>

			<Stars value={overall} onChange={setOverall} label="Overall experience" />

			{overall > 0 && (
				<>
					{dishes.length > 0 && (
						<div style={{ marginTop: 18 }}>
							<div className="ev-subtle" style={{ fontSize: 13, marginBottom: 8 }}>
								Rate the dishes too, if you like.
							</div>
							{dishes.map((dish) => (
								<div
									key={dish.id}
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										gap: 10,
										padding: '6px 0',
										borderTop: '1px solid rgba(17,17,19,0.06)',
									}}
								>
									<span style={{ fontSize: 14, flex: 1, minWidth: 0 }}>{dish.name}</span>
									<Stars
										value={perMeal[dish.id] || 0}
										onChange={(n) => setPerMeal((prev) => ({ ...prev, [dish.id]: n }))}
										size={22}
										label={dish.name}
									/>
								</div>
							))}
						</div>
					)}

					<textarea
						className="ev-input"
						rows={2}
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						placeholder="Anything you want the team to know? (optional)"
						maxLength={500}
						style={{ marginTop: 14, resize: 'vertical', fontFamily: 'inherit' }}
					/>

					{error && (
						<div className="ev-alert ev-alert-danger" style={{ marginTop: 10 }}>
							{error}
						</div>
					)}

					<button
						type="button"
						className="ev-primary-btn ev-top-gap"
						onClick={handleSubmit}
						disabled={submitting}
						style={{ width: '100%' }}
					>
						{submitting ? 'Sending…' : 'Send feedback'}
					</button>
				</>
			)}
		</div>
	);
};

export default MealRatingPrompt;
